'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock, Copy, Check, Zap, BarChart3, Users, Send } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const demoCredentials = [
  { role: 'Admin', email: 'marcus@mailflow.io', password: 'MailFlow2026!', name: 'Marcus Allen' },
  { role: 'Member', email: 'priya@mailflow.io', password: 'Member2026#', name: 'Priya Sharma' },
];

const features = [
  { icon: Send, label: 'Send up to 50K emails/day', sub: 'Reliable delivery at scale' },
  { icon: BarChart3, label: 'Real-time analytics', sub: 'Open rates, CTR, and bounce tracking' },
  { icon: Users, label: 'Subscriber list management', sub: 'Segmentation, tags, and imports' },
  { icon: Zap, label: 'Smart scheduling', sub: 'Send at the optimal time automatically' },
];

export default function LoginPage() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [authError, setAuthError] = useState('');

  const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm<LoginFormData>({
    defaultValues: { remember: false },
  });

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleUseCredentials = (cred: typeof demoCredentials[0]) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    setAuthError('');
  };

  const onSubmit = async (data: LoginFormData) => {
    setAuthError('');
    await new Promise((r) => setTimeout(r, 800));
    const matched = demoCredentials.find(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!matched) {
      setAuthError('Invalid credentials — use the demo accounts below to sign in');
      return;
    }
    const initials = matched.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
    localStorage.setItem('appnest_user', JSON.stringify({ name: matched.name, initials, role: matched.role, email: matched.email }));
    router.push('/');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-[46%] xl:w-[52%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #2a0f8f 0%, #5b35e8 35%, #7c3aed 65%, #c2410c 100%)' }}
      >
        {/* Animated background orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[-8%] right-[-8%] w-96 h-96 rounded-full opacity-25 float-orb"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }} />
          <div className="absolute bottom-[-5%] left-[-5%] w-72 h-72 rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, rgba(255,92,43,0.6) 0%, transparent 70%)', animationDelay: '3s' }} />
          <div className="absolute top-[40%] left-[10%] w-48 h-48 rounded-full opacity-10 float-orb"
            style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.8) 0%, transparent 70%)', animationDelay: '1.5s' }} />
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>

        {/* Logo & brand */}
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.3)' }}>
              <AppImage
                src="/assets/appnest-flow-logo.svg"
                alt="AppNest Flow logo"
                width={30}
                height={30}
                priority
                unoptimized
              />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-2xl font-extrabold text-white tracking-tight">AppNest</span>
              <span className="text-sm font-bold text-white/70 tracking-widest uppercase">Flow</span>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-white leading-tight mb-4">
            Where email campaigns<br />
            <span className="text-white/75">nest and take flight.</span>
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-sm">
            Send up to 50,000 emails daily with advanced segmentation, real-time analytics, and smart scheduling built for growing businesses.
          </p>

          {/* Decorative stat pills */}
          <div className="flex items-center gap-3 mt-8 flex-wrap">
            {['50K emails/day', '98.4% delivery', '34.7% open rate'].map((stat) => (
              <span key={stat} className="px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)' }}>
                ✦ {stat}
              </span>
            ))}
          </div>
        </div>

        {/* Feature list */}
        <div className="relative space-y-4">
          {features.map((f) => {
            const FIcon = f.icon;
            return (
              <div key={`feat-login-${f.label}`} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <FIcon size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{f.label}</p>
                  <p className="text-xs text-white/55">{f.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="relative">
          <p className="text-white/35 text-xs">© 2026 AppNest Flow Inc. All rights reserved.</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-background nest-bg">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #5b35e8, #ff5c2b)', boxShadow: '0 2px 10px rgba(91,53,232,0.35)' }}>
              <AppImage
                src="/assets/appnest-flow-logo.svg"
                alt="AppNest Flow logo"
                width={22}
                height={22}
                priority
                unoptimized
              />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-extrabold text-base" style={{ color: '#5b35e8' }}>AppNest</span>
              <span className="font-bold text-xs tracking-widest uppercase" style={{ color: '#ff5c2b' }}>Flow</span>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your AppNest Flow account</p>
          </div>

          {/* Auth error */}
          {authError && (
            <div className="mb-5 px-4 py-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{authError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div>
              <label className="label-base" htmlFor="loginEmail">Email Address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="loginEmail"
                  type="email"
                  placeholder="you@yourdomain.com"
                  className="input-base pl-9"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email address' },
                  })}
                />
              </div>
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label-base mb-0" htmlFor="loginPassword">Password</label>
                <button type="button" className="text-xs font-medium hover:underline" style={{ color: '#5b35e8' }}>
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="loginPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your password"
                  className="input-base pl-9 pr-10"
                  {...register('password', { required: 'Password is required' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password.message}</p>}
            </div>

            {/* Remember me */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-border"
                style={{ accentColor: '#5b35e8' }}
                {...register('remember')}
              />
              <span className="text-sm text-muted-foreground">Remember me for 30 days</span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full justify-center py-2.5 text-base rounded-xl font-bold text-white transition-all duration-150 hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #5b35e8 0%, #8b5cf6 60%, #ff5c2b 100%)', boxShadow: '0 4px 16px rgba(91,53,232,0.35)' }}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                  </svg>
                  Signing in…
                </>
              ) : 'Sign In to AppNest Flow'}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-8 p-4 rounded-xl border border-border bg-muted/40">
            <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Demo Accounts</p>
            <div className="space-y-2">
              {demoCredentials.map((cred) => (
                <div key={`demo-${cred.role}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-card border border-border">
                  <div className="min-w-0 flex-1">
                    <span className="badge bg-primary/10 text-primary text-xs mr-2">{cred.role}</span>
                    <span className="text-xs text-muted-foreground truncate">{cred.email}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleCopy(cred.email, `email-${cred.role}`)}
                      title="Copy email"
                      className="w-6 h-6 rounded flex items-center justify-center hover:bg-muted transition-all"
                    >
                      {copied === `email-${cred.role}` ? <Check size={11} className="text-green-600" /> : <Copy size={11} className="text-muted-foreground" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUseCredentials(cred)}
                      className="px-2.5 py-1 rounded text-xs font-semibold transition-all text-white"
                      style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don&apos;t have an account?{' '}
            <button type="button" className="font-semibold hover:underline" style={{ color: '#5b35e8' }}>
              Start free trial
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}